/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { NumberInput } from '@/components/blocks/NumberInput';
import { TableCellResponsiveContent } from '@/components/blocks/Table/TableCellResponsiveContent';
import { orderItemTableV2QuantityCellSX } from '@/components/content/OrderItemTableV2/styles/quantityCell';
import { orderItemTableV2TableCellResponsiveContentSX } from '@/components/content/OrderItemTableV2/styles/tableCellResponsiveContent';
import { useLocalization } from '@/data/Localization';
import { combineSX } from '@/utils/combineSX';
import { Typography } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { debounce } from 'lodash';
import { FC, useMemo } from 'react';
import { ProductType, ResponseProductAttribute } from '@/data/types/Product';
import { useProduct } from '@/data/Content/Product';
import { getAttributeValueByIdentifier } from '@/utils/productAttributes';
import { OrderTableData, OrderTableMeta } from '@/data/types/OrderItemTableV2';

export const OrderItemTableV2QuantityCell: FC<
	CellContext<OrderTableData, OrderTableData['quantity']>
> = ({ getValue, row, table }) => {
	const { meta } = table.options;
	const { readonly = false } = (meta ?? {}) as OrderTableMeta;
	const { quantity, onChange, min = 1, isControlled = false } = getValue();
	const localization = useLocalization('OrderItemTable');
	const EMPTY_ATTRS: ResponseProductAttribute[] = [];
	const { partNumber, contractId } = row.original.itemDetails;
	const EMPTY_PROD = {} as ProductType;
	const { product = EMPTY_PROD, loading } = useProduct({ id: partNumber, contractId });
	const {
		attributes = EMPTY_ATTRS,
	} = product;

	const maxOrderLimit:string = getAttributeValueByIdentifier(product?.attributes,"ITNL_MaximumOrderLimit");
	const debouncedQuantityChange = useMemo(
		() =>
			debounce((updatedQuantity) => {
				if (
					updatedQuantity !== null &&
					updatedQuantity > 0 &&
					updatedQuantity !== Number(quantity)
				) {
					onChange(updatedQuantity,maxOrderLimit,product.name);
				}
			}, 800),
		[onChange, quantity]
	);
	return (
		<TableCellResponsiveContent
			label={localization.Labels.Quantity.t()}
			sx={combineSX([orderItemTableV2TableCellResponsiveContentSX, orderItemTableV2QuantityCellSX])}
		>
			{readonly ? (
				<Typography>{quantity}</Typography>
			) : (
				<NumberInput
					id={`${row.id}-quantity`}
					data-testid={`${row.id}-quantity`}
					onChange={debouncedQuantityChange}
					value={quantity}
					min={min}
					showControls
					disallowEmptyOnBlur={true}
					disabled={readonly}
					isControlled={isControlled}
				/>
			)}
		</TableCellResponsiveContent>
	);
};
